import axios from 'axios'
import * as React from 'react'
import { useEffect, useState } from 'react'
// kept for the commented-out printButton below
import ReactToPrint from 'react-to-print'

const AccessRight = ({ user, printContentRef }) => {
  const [status, setStatus] = useState(user.collectionStatus || {})
  // key of the collection currently in-flight ('' when idle), used to disable buttons
  const [loading, setLoading] = useState('')

  const ownedAccess = (user.eventAccessList || []).filter((a) => a.hasAccess)
  const workshops = user.workshops || {}
  // Only attendees entitled to a badge may print one, and the lanyard and
  // congress bag go to the same people; without it both cards read "Not Purchased".
  const canPrint = !!user.canPrintNameBadge

  // Re-sync when a different delegate is scanned (component is reused, not remounted).
  useEffect(() => {
    setStatus(user.collectionStatus || {})
  }, [user.id])

  const renderFlag = (bool) =>
    bool ? (
      <i className="fas fa-check" style={{ color: 'green' }}></i>
    ) : (
      <i className="fas fa-times" style={{ color: 'red' }}></i>
    )

  // Light-red card shown when a collection doesn't apply to this delegate
  // (e.g. no congress pass, or no workshop bought for that slot).
  const notApplicableCard = (label) => (
    <div className="p-4 mt-2 card w-100" style={{ backgroundColor: '#f8d7da' }}>
      <span style={{ color: '#842029' }}>
        <i className="fas fa-times me-2"></i>
        {label}
      </span>
    </div>
  )

  const collectedFlag = (
    <span>
      {renderFlag(true)}
      <small className="ms-2">Collected</small>
    </span>
  )

  // Mark one onsite collection as done. `item` is the key the API whitelists,
  // `key` the matching field in collectionStatus.
  const collect = (item, key) => {
    setLoading(key)
    axios
      .post('/api/users/collect_item', { id: user.id, item })
      .then(({ data }) => {
        if (!data?.success) {
          alert(data?.message)
          return
        }
        setStatus((prev) => ({ ...prev, [key]: true }))
      })
      .catch((err) => alert(err))
      .finally(() => setLoading(''))
  }

  const collectButton = (item, key) => (
    <button
      className="btn btn-sm btn-outline-success"
      disabled={!!loading}
      onClick={() => collect(item, key)}
    >
      {loading === key ? 'Marking...' : 'Collect'}
    </button>
  )

  // Only feeds the commented-out printButton below, where it runs after the
  // print dialog closes. The live name badge card goes through collect_item
  // like every other collection; /print_badge marks the same flag and logs the
  // same attendance, so the two paths stay interchangeable.
  const handlePrinted = () => {
    setLoading('badgeCollected')
    axios
      .get(`/api/users/print_badge?id=${user.id}`)
      .then(({ data }) => {
        if (!data?.success) {
          alert(data?.message)
          return
        }
        setStatus((prev) => ({ ...prev, badgeCollected: true }))
      })
      .catch((err) => alert(err))
      .finally(() => setLoading(''))
  }

  // const printButton = (label) => (
  //   <ReactToPrint
  //     onAfterPrint={handlePrinted}
  //     trigger={() => <button className="btn btn-sm btn-danger">{label}</button>}
  //     content={() => printContentRef?.current}
  //   />
  // )

  // Workshop card: the workshop(s) the delegate registered for in this slot,
  // plus the collect button. Workshops come off the products on their paid
  // orders, so a manually granted access flag can show none.
  const workshopCard = (session, item, key) => {
    const { hasAccess, items = [] } = workshops[session] || {}

    if (!hasAccess && items.length === 0 && !status[key]) {
      return notApplicableCard('Not Purchased')
    }

    return (
      <div className="p-4 mt-2 card w-100">
        {items.length > 0 ? (
          items.map((workshop) => (
            <div key={workshop.id} className="mb-2">
              <div className="fw-bold">{workshop.title}</div>
              {workshop.code && (
                <small className="text-muted d-block">{workshop.code}</small>
              )}
              {workshop.timeslot && (
                <small className="text-muted d-block">{workshop.timeslot}</small>
              )}
            </div>
          ))
        ) : (
          <small className="mb-2 text-muted d-block">
            Access granted, no workshop on record
          </small>
        )}
        <div>{status[key] ? collectedFlag : collectButton(item, key)}</div>
      </div>
    )
  }

  return (
    <>
      {/* 0. Delegate reference — the user id the counter quotes when escalating,
          plus any remark left on the account (special handling, notes). */}
      <div className="mt-3">User Details:</div>
      <div className="p-4 mt-2 card w-100">
        <h2 className="text-muted">User ID: {user.id}</h2>
        {user.remark && (
          <h4>{user.remark}</h4>
        )}
      </div>

      {/* 1. Name badge collected — the collect button lives here until collected.
          An already-collected badge always reports as collected, even for someone
          who wouldn't be entitled to one now, so the counter doesn't hand out a
          second one. Same for the lanyard below. */}
      <div className="mt-3">Name Badge Collected:</div>
      {status.badgeCollected ? (
        <div className="p-4 mt-2 card w-100">{collectedFlag}</div>
      ) : !canPrint ? (
        notApplicableCard('Not Purchased')
      ) : (
        <div className="p-4 mt-2 card w-100">
          {/* printButton('Click Here to Print') */}
          {collectButton('name_badge', 'badgeCollected')}
        </div>
      )}

      {/* 2. Lanyard and congress bag, handed out to everyone who gets a badge */}
      <div className="mt-3">Lanyard &amp; Congress Bag Collected:</div>
      {status.lanyardBagCollected ? (
        <div className="p-4 mt-2 card w-100">{collectedFlag}</div>
      ) : !canPrint ? (
        notApplicableCard('Not Purchased')
      ) : (
        <div className="p-4 mt-2 card w-100">
          {collectButton('lanyard_bag', 'lanyardBagCollected')}
        </div>
      )}

      {/* 3. Event access — every access flag the delegate holds */}
      <div className="mt-3">Event Access:</div>
      <div className="p-4 mt-2 card w-100">
        {ownedAccess.length > 0 ? (
          ownedAccess.map((access, i) => (
            <div key={access.name || i} className="mb-1">
              {renderFlag(true)}
              <small className="ms-2">{access.label}</small>
            </div>
          ))
        ) : (
          <small className="text-muted">No access</small>
        )}
      </div>

      {/* 4 & 5. Workshops registered for, per timeslot */}
      <div className="mt-3">Workshop (AM):</div>
      {workshopCard('am', 'workshop_am', 'workshopAmCollected')}

      <div className="mt-3">Workshop (PM):</div>
      {workshopCard('pm', 'workshop_pm', 'workshopPmCollected')}
    </>
  )
}

export default AccessRight
